import { Image } from 'expo-image';
import React, { useRef } from 'react';
import { Pressable, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { RatingRing } from '@/components/ui/RatingRing';
import { useThemePalette } from '@/config/theme';
import { type DraftPick } from '@/features/fantasy/squadModel';
import { formatPrice } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { PLAYER_TOKEN_WIDTH } from '@/lib/pitch/formation';

/** Shared with every other pitch token so fantasy columns line up identically. */
export const CHIP_WIDTH = PLAYER_TOKEN_WIDTH;
const RING_SIZE = 56;
const PHOTO_SIZE = 48;

export interface ChipDragHandlers {
  /** Called as the finger lifts; receives window coords of the drop point. */
  onDrop: (playerId: string, absoluteX: number, absoluteY: number) => void;
  /** Re-measure drop targets right before a drag starts. */
  onDragStart: () => void;
}

/**
 * Player headshot inside the rating ring. When the photo is missing OR fails to
 * load, falls back to the player's NATION FLAG (their fantasy "club" at the World
 * Cup), then to initials. `flagUri` is the team/nation crest.
 */
function PlayerPhoto({
  uri,
  flagUri,
  name,
  showPhotos = true,
}: {
  uri: string | null;
  flagUri: string | null;
  name: string;
  /** When false, skip the headshot and show the crest/flag directly. */
  showPhotos?: boolean;
}) {
  const [failed, setFailed] = React.useState(false);
  const showPhoto = showPhotos && uri && !failed;

  if (!showPhoto && flagUri) {
    return (
      <View
        className="items-center justify-center overflow-hidden rounded-full bg-surface-2"
        style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
      >
        <Image
          source={{ uri: flagUri }}
          style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
          contentFit="contain"
          transition={150}
          cachePolicy="memory-disk"
          accessibilityLabel={name}
        />
      </View>
    );
  }
  if (!showPhoto) {
    const initials = name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
    return (
      <View
        className="items-center justify-center rounded-full bg-surface-2"
        style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
      >
        <AppText variant="caption" weight="bold" className="text-soft">
          {initials}
        </AppText>
      </View>
    );
  }
  return (
    <View
      className="overflow-hidden rounded-full bg-surface-2"
      style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
    >
      <Image
        source={{ uri: uri as string }}
        style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
        contentFit="cover"
        contentPosition="top"
        transition={150}
        cachePolicy="memory-disk"
        onError={() => setFailed(true)}
        accessibilityLabel={name}
      />
    </View>
  );
}

/**
 * World-Cup-fantasy style squad token: the player's photo inside a volt
 * rating ring (captain ring goes gold - the only non-volt accent), name pill
 * and price/points underneath. Tap opens the action menu; hold-and-drag
 * (spring physics) swaps with a compatible player.
 */
export function PlayerChip({
  pick,
  subtitle,
  subtitleTone = 'price',
  /** 0..100 ring fill; defaults to the price-derived rating. */
  ringPercent,
  showPhotos = true,
  onPress,
  drag,
  registerLayout,
}: {
  pick: DraftPick;
  subtitle?: string;
  subtitleTone?: 'price' | 'points' | 'muted';
  ringPercent?: number;
  /** false → show the team crest/nation flag instead of the headshot. */
  showPhotos?: boolean;
  onPress?: (playerId: string) => void;
  drag?: ChipDragHandlers;
  registerLayout?: (playerId: string, node: View | null) => void;
}) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const containerRef = useRef<View>(null);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(1);
  const dragging = useSharedValue(0);

  const player = pick.player;
  const displayName = player.known_as ?? player.name;
  const label = subtitle ?? formatPrice(player.fantasy_price, numeralOptions);
  // A clean FULL ring just frames the photo (volt, or gold for the captain) - no
  // partial "stat arc" or OVR number. Players are judged by PRICE only; the only
  // number under the chip is the price (or points in the read-only My Team view).
  const percent = ringPercent ?? 100;

  const pan = Gesture.Pan()
    .enabled(Boolean(drag))
    .activateAfterLongPress(180)
    .onStart(() => {
      dragging.value = 1;
      scale.value = withSpring(1.12);
      if (drag) runOnJS(drag.onDragStart)();
    })
    .onUpdate((event) => {
      translateX.value = event.translationX;
      translateY.value = event.translationY;
    })
    .onEnd((event) => {
      if (drag) {
        runOnJS(drag.onDrop)(player.id, event.absoluteX, event.absoluteY);
      }
    })
    .onFinalize(() => {
      dragging.value = 0;
      translateX.value = withSpring(0, { damping: 14 });
      translateY.value = withSpring(0, { damping: 14 });
      scale.value = withSpring(1);
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
    zIndex: dragging.value === 1 ? 50 : 0,
    elevation: dragging.value === 1 ? 8 : 0,
  }));

  return (
    <GestureDetector gesture={pan}>
      <Animated.View style={animatedStyle}>
        <View
          ref={(node) => {
            containerRef.current = node;
            registerLayout?.(player.id, node);
          }}
          collapsable={false}
          style={{ width: CHIP_WIDTH }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={displayName}
            onPress={() => onPress?.(player.id)}
            className="items-center gap-1"
          >
            <View>
              {/* Captain gold is the ONLY non-volt accent (Volt design rule). */}
              <RatingRing
                size={RING_SIZE}
                percent={percent}
                strokeWidth={2.5}
                color={pick.isCaptain ? palette.captain : palette.volt}
              >
                <PlayerPhoto
                  uri={player.photo_url}
                  flagUri={player.team?.logo_url ?? null}
                  name={displayName}
                  showPhotos={showPhotos}
                />
              </RatingRing>
              {pick.isCaptain || pick.isVice ? (
                <View
                  className={`absolute -end-0.5 -top-0.5 h-5 w-5 items-center justify-center rounded-md ${
                    pick.isCaptain ? 'bg-captain' : 'bg-volt'
                  }`}
                >
                  <AppText variant="micro" weight="bold" className="text-on-volt">
                    {pick.isCaptain ? 'C' : 'V'}
                  </AppText>
                </View>
              ) : null}
            </View>
            <View className="rounded-full bg-inset px-2 py-0.5" style={{ maxWidth: CHIP_WIDTH }}>
              <AppText variant="micro" weight="bold" className="text-ink" numberOfLines={1}>
                {player.known_as ?? player.name.split(' ').slice(-1)[0]}
              </AppText>
            </View>
            <AppText
              variant="micro"
              weight="bold"
              tabular
              className={
                subtitleTone === 'points'
                  ? 'text-volt-bright'
                  : subtitleTone === 'muted'
                    ? 'text-muted'
                    : 'text-soft'
              }
            >
              {label}
            </AppText>
          </Pressable>
        </View>
      </Animated.View>
    </GestureDetector>
  );
}

/** Dashed placeholder for an unfilled squad slot. */
export function EmptySlotChip({
  positionLabel,
  onPress,
}: {
  positionLabel: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={positionLabel}
      onPress={onPress}
      className="items-center gap-1.5"
      style={{ width: CHIP_WIDTH }}
    >
      <View
        className="items-center justify-center rounded-full border-2 border-dashed border-volt-line bg-surface/60"
        style={{ width: RING_SIZE, height: RING_SIZE }}
      >
        <AppText variant="heading" className="text-volt">
          +
        </AppText>
      </View>
      <AppText variant="micro" weight="bold" className="text-muted">
        {positionLabel}
      </AppText>
    </Pressable>
  );
}
